import { useEffect, useState } from "react";
import { getAccessToken, gateConfig, gateAccess } from "../../lib/survey-api";

/**
 * Anonymous passcode gate. If the deployment has a shared access code set, this
 * shows a code-entry screen before the survey. A correct code stores a token for
 * the session and reveals the children. If no code is configured, it passes
 * straight through. The code attests access, not identity, so anonymity holds.
 */
export function AccessGate({ children }: { children: React.ReactNode }) {
  // A returning user with a token never sees the checking flash.
  const [status, setStatus] = useState<"checking" | "locked" | "open">(() =>
    getAccessToken() ? "open" : "checking",
  );
  const [code, setCode] = useState("");
  const [wrong, setWrong] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (getAccessToken()) {
      setStatus("open");
      return;
    }
    let alive = true;
    gateConfig().then((c) => {
      if (alive) setStatus(c.required ? "locked" : "open");
    });
    return () => {
      alive = false;
    };
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    setWrong(false);
    const r = await gateAccess(code.trim());
    setBusy(false);
    if (r.ok) setStatus("open");
    else setWrong(true);
  };

  if (status === "checking") {
    return (
      <main className="min-h-screen flex items-center justify-center" aria-busy="true">
        <p className="text-[11px] uppercase tracking-[0.24em] text-ink-300 breath">Nellis Auction</p>
      </main>
    );
  }

  if (status === "locked") {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <div className="max-w-sm w-full space-y-6">
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-[0.24em] text-ink-300">Nellis Auction</p>
            <h1 className="font-display text-3xl text-ink-900">Enter the access code</h1>
            <p className="text-ink-500 text-sm leading-relaxed">
              Shared with leadership. This stays completely anonymous, the code is not tied to you.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <input
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setWrong(false);
              }}
              autoFocus
              placeholder="Access code"
              className="w-full text-center tracking-[0.3em] rounded-xl border border-parchment-300 bg-parchment-50 px-4 py-3 text-ink-900 placeholder:tracking-normal placeholder:text-ink-300/80 focus:outline-none focus:border-ink-300"
            />
            {wrong && <p className="text-xs text-accent-600 shake">That code did not work. Try again.</p>}
            <button
              type="submit"
              disabled={!code.trim() || busy}
              className="w-full px-8 py-3 rounded-full text-sm font-medium bg-ink-900 text-parchment-50 hover:bg-ink-700 disabled:bg-parchment-200 disabled:text-ink-300 transition-all"
            >
              {busy ? "Checking…" : "Enter"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return <>{children}</>;
}
